import React, { useCallback, useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import { apiClient } from '../../api/apiClient';
import PayslipPasswordGate from './payslips/PayslipPasswordGate';
import PayslipDetail from './payslips/PayslipDetail';

const fmt = (n) => Number(n || 0).toLocaleString('ko-KR');

function tokenHeaders(token) {
  return { 'x-payslip-token': token };
}

export default function MyPayslipsPage() {
  const [sessionToken, setSessionToken] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [error, setError] = useState('');
  const [payslips, setPayslips] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const resetSession = useCallback((message = '급여명세서 확인이 만료되었습니다. 다시 입력해 주세요.') => {
    setSessionToken('');
    setPayslips([]);
    setSelectedId(null);
    setDetail(null);
    setError(message);
  }, []);

  const loadList = useCallback(async (token) => {
    setLoadingList(true);
    const res = await apiClient.get('/api/my/payslips', { headers: tokenHeaders(token) });
    setLoadingList(false);
    if (!res.ok) {
      resetSession(res.data?.message || undefined);
      return;
    }

    const rows = res.data?.payslips || [];
    setPayslips(rows);
    setSelectedId(rows[0]?.record_id || null);
  }, [resetSession]);

  async function verify(password) {
    setError('');
    setVerifyLoading(true);
    const res = await apiClient.post('/api/my/payslips/verify', { password });
    setVerifyLoading(false);

    if (!res.ok || !res.data?.token) {
      setError(res.data?.message || '비밀번호 확인에 실패했습니다.');
      return;
    }

    setSessionToken(res.data.token);
    await loadList(res.data.token);
  }

  useEffect(() => {
    if (!sessionToken || !selectedId) {
      setDetail(null);
      return;
    }

    let active = true;
    (async () => {
      setLoadingDetail(true);
      const res = await apiClient.get(`/api/my/payslips/${selectedId}`, { headers: tokenHeaders(sessionToken) });
      if (!active) return;
      setLoadingDetail(false);

      if (!res.ok) {
        resetSession(res.data?.message || undefined);
        return;
      }
      setDetail(res.data?.data || null);
    })();

    return () => { active = false; };
  }, [selectedId, sessionToken, resetSession]);

  if (!sessionToken) {
    return <PayslipPasswordGate onVerify={verify} loading={verifyLoading} error={error} />;
  }

  return (
    <div className="p-4 sm:p-5 max-w-[790px] mx-auto">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-extrabold text-[var(--text)] flex items-center gap-2">
            <Wallet size={20} />
            급여명세서
          </h1>
          <p className="text-xs text-[var(--text-muted)] mt-1">확정된 월 급여만 표시됩니다.</p>
        </div>
        <button
          type="button"
          onClick={() => resetSession('')}
          className="h-8 px-3 rounded-lg border border-[var(--border)] text-xs font-semibold text-[var(--text-sub)] hover:bg-[var(--bg-subtle)]"
        >
          잠금
        </button>
      </div>

      {loadingList ? (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-8 text-center text-sm text-[var(--text-muted)]">
          급여명세서를 불러오는 중입니다.
        </div>
      ) : payslips.length === 0 ? (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-8 text-center text-sm text-[var(--text-muted)]">
          확인 가능한 확정 급여명세서가 없습니다.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[210px_minmax(0,540px)] gap-4 lg:justify-center lg:items-start">
          <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-2 h-fit lg:sticky lg:top-4">
            {payslips.map((row) => {
              const active = Number(selectedId) === Number(row.record_id);
              return (
                <button
                  key={row.record_id}
                  type="button"
                  onClick={() => setSelectedId(row.record_id)}
                  className={`w-full text-left rounded-lg px-3 py-2.5 mb-1 last:mb-0 border transition-colors ${
                    active
                      ? 'border-accent-500 bg-accent-500/10'
                      : 'border-transparent hover:bg-[var(--bg-subtle)]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-extrabold text-[var(--text)]">{row.year}년 {row.month}월</span>
                    <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400">확정</span>
                  </div>
                  <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{row.company_name || row.company_code}</div>
                  <div className="text-xs font-bold text-[var(--text-sub)] mt-1">실수령 {fmt(row.net_pay)}원</div>
                </button>
              );
            })}
          </div>

          <div className="min-w-0 w-full max-w-[540px]">
            {loadingDetail ? (
              <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-8 text-center text-sm text-[var(--text-muted)]">
                상세를 불러오는 중입니다.
              </div>
            ) : (
              <PayslipDetail detail={detail} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
